/**
 * Данные, взятые из карточки сделки (ТЗ FR-06).
 *
 * Новое дело предзаполняется само — вводить второй раз то, что менеджер уже
 * внёс в чек-лист, бессмысленно. Но подставленное надо показать: за то, что
 * попадёт в заявление, отвечает юрист, а не менеджер. Отсюда две задачи
 * у этой полосы — сказать, что подставлено, и дать посмотреть источник
 * каждого значения.
 *
 * Для дела, которое уже заполнялось, предзаполнения не происходит: там
 * предлагается кнопка, и решает юрист.
 */

import { useState } from 'react';
import type { CaseProfile } from '@bd/domain';
import { applyToProfile, type CrmSnapshot, type FoundValue } from '../../storage/crm-import.js';

function SourceTable({ snapshot }: { snapshot: CrmSnapshot }) {
  return (
    <table className="simple-table">
      <thead>
        <tr>
          <th>Поле сделки</th>
          <th>Куда попало</th>
          <th>Значение</th>
        </tr>
      </thead>
      <tbody>
        {snapshot.found.map((value) => (
          <tr key={value.target}>
            <td>{value.sourceLabel}</td>
            <td>{value.title}</td>
            <td>{value.value}</td>
          </tr>
        ))}
        {snapshot.skipped.map((value, i) => (
          <tr key={`skip-${i}`} className="archived">
            <td>{value.sourceLabel}</td>
            <td colSpan={2}>не переносится: {value.reason}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CrmImportBanner({
  snapshot,
  profile,
  readOnly,
  prefilled,
  onApply,
}: {
  /**
   * Что портал может дать делу. Снимок приходит готовым, а не читается
   * здесь заново: источник у подстановки и у этой полосы должен быть один,
   * иначе полоса перечисляет не то, что на самом деле подставлено.
   */
  snapshot: CrmSnapshot | null;
  profile: CaseProfile;
  readOnly: boolean;
  /** Что подставлено автоматически при открытии нового дела */
  prefilled: FoundValue[];
  onApply: (profile: CaseProfile) => void;
}) {
  const [applied, setApplied] = useState<FoundValue[] | null>(null);
  const [details, setDetails] = useState(false);

  if (!snapshot || snapshot.found.length === 0) return null;

  const shown = applied ?? (prefilled.length > 0 ? prefilled : null);

  if (shown) {
    return (
      <div className="banner banner-hint">
        <strong>Подставлено из портала: </strong>
        {shown.map((value) => value.title).join(', ')}.{' '}
        <button type="button" className="link-button" onClick={() => setDetails((open) => !open)}>
          {details ? 'Скрыть' : 'Проверить значения'}
        </button>
        <span className="hint">
          Проверьте их: чек-лист и карточку контакта заполняет менеджер, а за содержание заявления отвечаете вы.
          Всё подставленное можно исправить в блоках дела как обычно.
        </span>
        {details && <SourceTable snapshot={snapshot} />}
      </div>
    );
  }

  const apply = () => {
    const result = applyToProfile(profile, snapshot.found);
    onApply(result.profile);
    setApplied(result.filled.length > 0 ? result.filled : []);
  };

  /**
   * Чего в деле действительно нет.
   *
   * Перечислять всё найденное нельзя: портал отдаёт ФИО почти всегда —
   * оно есть в карточке контакта, — и полоса «есть данные, которых нет
   * в деле: Фамилия, Имя, Отчество» висела бы над каждым заполненным делом.
   * Юрист привыкает её не читать, и настоящая находка теряется среди ложных.
   */
  const missing = applyToProfile(profile, snapshot.found).filled;
  if (missing.length === 0) return null;

  // Дело уже заполнялось: подставлять молча нельзя, но предложить стоит —
  // поля могли добавить в сделку уже после начала работы
  return (
    <div className="banner banner-hint">
      В портале есть данные, которых нет в деле:{' '}
      {missing.map((value) => value.title).join(', ')}.
      <button type="button" className="link-button" disabled={readOnly} onClick={apply}>
        Перенести
      </button>
      <button type="button" className="link-button" onClick={() => setDetails((open) => !open)}>
        {details ? 'Скрыть' : 'Что именно'}
      </button>
      {details && <SourceTable snapshot={snapshot} />}
      <span className="hint">Заполнятся только пустые поля — введённое вами не перезапишется.</span>
    </div>
  );
}
